'use client';

import { useEffect, useRef } from 'react';
import { useDocumentViewer } from './viewer-context';
import { ViewerResizeHandle } from './viewer-resize-handle';
import { DocumentViewerView } from './document-viewer-view';

/**
 * The app-wide document viewer: the shared `DocumentViewerView` in a fixed,
 * dockable, resizable dialog, driven by `useDocumentViewer()`. Mounted once in
 * the root providers; renders nothing while closed, so closing ends the
 * document's per-document state with the view (spec 2026-10-08 D3).
 */
export function DocumentViewerPanel() {
  const {
    source, close, dockStyle, setDockStyle, embedInsetRight, locateRequest,
    guidanceRail, setGuidanceRail, panelWidth,
  } = useDocumentViewer();
  const documentKey = source ? (source.documentKey ?? source.pdfUrl) : null;

  // Focus management. Not a full focus trap (that belongs to a shared dialog
  // primitive), but enough that the panel is operable and dismissible from
  // the keyboard and does not strand focus behind the overlay: move focus in
  // on open, and hand it back to whatever had it when the panel closes.
  const panelRef = useRef<HTMLElement | null>(null);
  const restoreFocusTo = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!documentKey) return;
    const active = document.activeElement;
    // `document.body` is where focus sits when nothing in particular has it —
    // restoring TO it is a no-op worth skipping rather than a place to return.
    restoreFocusTo.current =
      active instanceof HTMLElement && active !== document.body ? active : null;
    panelRef.current?.focus();
    return () => {
      restoreFocusTo.current?.focus?.();
      restoreFocusTo.current = null;
    };
  }, [documentKey]);

  if (!source) return null;

  const embedded = dockStyle === 'embedded';

  return (
    <>
      {/* The scrim belongs to overlay only. Embedded means the page stays
          usable BESIDE the document — dimming it, or closing the viewer on a
          click into it, would defeat the whole point of the mode. */}
      {!embedded && (
        <div className="fixed inset-0 z-40 bg-black/20" onClick={close} aria-hidden="true" />
      )}
      <DocumentViewerView
        source={source}
        onClose={close}
        dockStyle={dockStyle}
        onToggleDockStyle={() => setDockStyle(embedded ? 'overlay' : 'embedded')}
        locateRequest={locateRequest}
        guidanceRail={guidanceRail}
        onToggleGuidanceRail={() =>
          setGuidanceRail(guidanceRail === 'collapsed' ? 'expanded' : 'collapsed')
        }
        as="aside"
        rootRef={panelRef}
        rootProps={{
          role: 'dialog',
          // Modal only while it overlays: an embedded panel sits alongside
          // content the caseworker is meant to keep reading and reaching, and
          // aria-modal there would tell a screen reader the rest of the page is
          // inert when it is not.
          'aria-modal': !embedded,
          'aria-label': source.title,
          tabIndex: -1,
          'data-dock-style': dockStyle,
          // Fixed in BOTH styles — this panel is mounted once in the root
          // providers, above the dashboard shell, and never moves (moving it
          // would remount it and drop the page/zoom/finder state). What makes
          // "embedded" a column is the shell reserving `reservedRight` (this
          // same `panelWidth`) as <main>'s right margin, so the page reflows
          // beside it instead of under it.
          style: { width: panelWidth, right: embedded ? embedInsetRight : 0 },
          className: `fixed inset-y-0 z-50 flex max-w-full flex-col border-l border-line bg-surface outline-none ${
            embedded ? 'shadow-none' : 'shadow-xl'
          }`,
        }}
        leading={<ViewerResizeHandle />}
      />
    </>
  );
}
